import { HttpClient } from '@angular/common/http';
import { EnvironmentProviders, inject, provideAppInitializer, Provider } from '@angular/core';
import { I18nApiService } from '@app/api/services/i18n-api.service';
import { Lang } from '@app/core/i18n/classes/lang';
import { CustomMissingTranslationHandler } from '@app/core/i18n/services/custom-missing-translation-handler.service';
import { DefaultJsonFileLoader } from '@app/core/i18n/services/default-json-file-loader.service';
import { JsonFileLoader } from '@app/core/i18n/services/json-file-loader.service';
import { OooCompilerService } from '@app/core/i18n/services/ooo-compiler.service';
import { TranslationManagementService } from '@app/core/i18n/services/translation-management.service';
import { BrowserLocaleService } from '@app/core/i18n/services/browser-locale.service';
import { DayjsLocaleService } from '@app/core/i18n/services/dayjs-locale.service';
import { PrimeNGLocaleService } from '@app/core/i18n/services/primeng-locale.service';
import { LocalStorageService } from '@app/core/services/local-storage/local-storage.service';
import { LocaleNumberPipe } from '@app/shared/pipes/locale-number/locale-number.pipe';
import { LocalePercentPipe } from '@app/shared/pipes/locale-number/locale-percent.pipe';
import { LocaleCurrencyPipe } from '@app/shared/pipes/locale-number/locale-currency.pipe';
import {
  MissingTranslationHandler,
  provideTranslateService,
  RootTranslateServiceConfig,
  TranslateCompiler,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environment';

export function provideI18n(): (Provider | EnvironmentProviders)[] {
  const translateConfig: RootTranslateServiceConfig = {
    loader: {
      provide: TranslateLoader,
      useClass: JsonFileLoader,
      deps: [HttpClient, I18nApiService, DefaultJsonFileLoader],
    },
    missingTranslationHandler: {
      provide: MissingTranslationHandler,
      useFactory: () => new CustomMissingTranslationHandler(),
    },
  };

  if (!environment.production) {
    const translationManagementService = new TranslationManagementService(new LocalStorageService());

    if (translationManagementService.isOooEnabled()) {
      translateConfig.compiler = {
        provide: TranslateCompiler,
        useClass: OooCompilerService,
      };
    }
  }

  return [
    provideTranslateService(translateConfig),
    LocaleNumberPipe,
    LocalePercentPipe,
    LocaleCurrencyPipe,
    provideAppInitializer(() => {
      const translateService = inject(TranslateService);
      const browserLocaleService = inject(BrowserLocaleService);
      const dayjsLocaleService = inject(DayjsLocaleService);
      const primeNGLocaleService = inject(PrimeNGLocaleService);

      const browserLang = browserLocaleService.getLocale().lang;

      const selectedLang = new Lang().selectAvailableLang(browserLang);

      translateService.addLangs([selectedLang]);

      return firstValueFrom(translateService.use(selectedLang)).then(() => {
        // Initialize dayjs locale after translation service is ready
        dayjsLocaleService.init();
        // Initialize PrimeNG locale after translation service is ready
        primeNGLocaleService.init();
      });
    }),
  ];
}
